
import React, { useState } from 'react';
import { MOCK_BOOKINGS } from '../constants';
import { BookingStatus, PaymentStatus } from '../types';

const AllBookings: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const getStatusColor = (status: BookingStatus) => {
    switch (status) {
      case BookingStatus.CONFIRMED: return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400';
      case BookingStatus.PENDING: return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400';
      case BookingStatus.CANCELLED: return 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getPaymentColor = (status: PaymentStatus) => {
    switch (status) {
      case PaymentStatus.PAID: return 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400';
      case PaymentStatus.PARTIAL: return 'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400';
      case PaymentStatus.PENDING: return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="p-8 space-y-6 max-w-[1600px] mx-auto w-full">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">Master Booking List</h2>
          <p className="text-slate-500 dark:text-slate-400">Pesquisa avançada e gerenciamento de todas as reservas</p>
        </div>
        <button className="bg-primary hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all font-medium shadow-md shadow-blue-500/20 active:scale-95">
          <span className="material-icons-round text-[20px]">file_download</span> Exportar CSV
        </button>
      </div>

      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm">
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-500 uppercase">Procurar</label>
            <div className="relative">
              <span className="material-icons-round absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">search</span>
              <input 
                className="w-full pl-9 pr-4 py-2 text-sm rounded-lg border-slate-200 dark:border-slate-700 dark:bg-slate-800 focus:ring-primary focus:border-primary" 
                placeholder="ID, Cliente, Tour..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-500 uppercase">Data de Operação</label>
            <input className="w-full rounded-lg border-slate-200 dark:border-slate-700 dark:bg-slate-800 focus:ring-primary text-sm" type="date" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-500 uppercase">Status</label>
            <select className="w-full rounded-lg border-slate-200 dark:border-slate-700 dark:bg-slate-800 focus:ring-primary text-sm">
              <option>Todos os Status</option>
              <option>Confirmado</option>
              <option>Pendente</option>
            </select>
          </div>
          <div className="flex items-end">
            <button className="w-full text-primary border border-primary/20 hover:bg-primary/5 py-2 rounded-lg text-sm font-medium transition-colors">
              Limpar Filtros
            </button>
          </div>
        </div>
      </section>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase bg-slate-50 dark:bg-slate-800/50">
                <th className="px-6 py-4 border-b border-slate-200 dark:border-slate-800">ID</th>
                <th className="px-6 py-4 border-b border-slate-200 dark:border-slate-800">Data & Hora</th>
                <th className="px-6 py-4 border-b border-slate-200 dark:border-slate-800">Cliente</th>
                <th className="px-6 py-4 border-b border-slate-200 dark:border-slate-800">Tour</th>
                <th className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 text-center">PAX</th>
                <th className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 text-right">Preço</th>
                <th className="px-6 py-4 border-b border-slate-200 dark:border-slate-800">Reserva</th>
                <th className="px-6 py-4 border-b border-slate-200 dark:border-slate-800">Pagamento</th>
                <th className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 text-center">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {MOCK_BOOKINGS.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors group">
                  <td className="px-6 py-4">
                    <span className="font-mono text-xs font-bold text-slate-700 dark:text-slate-300 uppercase">{row.id}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <p className="font-medium dark:text-white">{row.date}</p>
                      <p className="text-xs text-slate-500 flex items-center gap-1">
                        <span className="material-icons-round text-[12px]">schedule</span> {row.time}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <p className="font-medium dark:text-white">{row.customerName}</p>
                      <p className="text-xs text-slate-500">{row.origin}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm font-medium dark:text-white">{row.tour}</p>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="inline-flex items-center justify-center w-7 h-7 bg-slate-100 dark:bg-slate-800 rounded-full text-xs font-bold dark:text-white">
                      {row.pax}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <p className="text-sm font-bold dark:text-white">{row.currency} {row.price.toLocaleString('pt-BR')}</p>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-0.5 text-[10px] font-bold rounded-full uppercase tracking-tight ${getStatusColor(row.status)}`}>
                      {row.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-0.5 text-[10px] font-bold rounded-full uppercase tracking-tight ${getPaymentColor(row.payment)}`}>
                      {row.payment}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-2">
                      <button className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded text-slate-500 hover:text-primary transition-colors">
                        <span className="material-icons-round text-[18px]">visibility</span>
                      </button>
                      <button className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded text-slate-500 hover:text-primary transition-colors">
                        <span className="material-icons-round text-[18px]">edit</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-800/30">
          <p className="text-sm text-slate-500">Mostrando 1 a 4 de {MOCK_BOOKINGS.length} reservas</p>
          <div className="flex items-center gap-1">
            <button className="p-2 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50" disabled>
              <span className="material-icons-round text-[20px]">chevron_left</span>
            </button>
            <button className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium">1</button>
            <button className="p-2 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
              <span className="material-icons-round text-[20px]">chevron_right</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllBookings;
